import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Child,
  MedicalData,
  MedicalHistory,
  createMedicalHistory,
  fetchChildren,
  fetchMedicalData,
  fetchMedicalHistories
} from '../api/client';
import { useAuthStore } from '../stores/auth';

export function AddMedicalHistory() {
  const { t } = useTranslation(['common', 'medicalHistory']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [success, _setSuccess] = useState(false);
  const [children, setChildren] = useState<Child[]>([]);
  const [filteredChildren, setFilteredChildren] = useState<Child[]>([]);
  const [medicalHistories, setMedicalHistories] = useState<MedicalHistory[]>([]);
  const [medicalData, setMedicalData] = useState<MedicalData | null>(null);
  const { token, parent } = useAuthStore();
  const currentYear = new Date().getFullYear();
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [showConsentWarningDialog, setShowConsentWarningDialog] = useState(false);
  const [showSubmitConfirmDialog, setShowSubmitConfirmDialog] = useState(false);
  const [showGdprInfo, setShowGdprInfo] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const consentWarningRef = useRef<HTMLDivElement>(null);
  const submitConfirmRef = useRef<HTMLDivElement>(null);

  // Effect to handle clicks outside the terms modal
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setShowTermsModal(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  // Form state
  const [formData, setFormData] = useState({
    child: 0,
    year: currentYear,
    weight: "",
    blood_type: "",
    
    // Vaccination information
    is_vaccinated: true,
    missing_vaccines: [] as number[],
    other_missing_vaccines: "",
    
    // Known diseases
    has_any_known_disease: false,
    known_diseases: [] as number[],
    other_known_diseases: "",
    
    // Allergies
    has_allergies: false,
    allergies: [] as number[],
    other_allergies: "",
    
    // Major disease
    has_any_major_disease: false,
    major_disease: "",
    
    // Psychological disorders
    has_psychological_disorder: false,
    psychological_disorders: [] as number[],
    other_psychological_disorders: "",
    
    // Medication
    is_receiving_medication: false,
    medication: "",
    
    // Development disorders
    has_any_development_disorder: false,
    development_disorders: [] as number[],
    other_development_disorders: "",
    
    // Specialist
    is_being_watched_by_specialist: false,
    specialist_contact_info: "",
    
    // Health issues
    has_worth_mentioning_health_issues: false,
    worth_mentioning_health_issues: "",
    
    // Risk group
    belongs_to_risk_group: false,
    risk_group_explanation: "",
    
    // Underlying diseases
    has_underlying_disease: false,
    underlying_diseases: [] as number[],
    other_underlying_diseases: "",

    // GDPR Consent fields
    medical_data_acknowledged: true,
    personal_data_acknowledged: true,
    medical_consent_level: 'full' as 'full' | 'partial' | 'none',
    medical_consent_explanation: '',
    personal_consent_level: 'full' as 'full' | 'partial' | 'none',
    personal_consent_explanation: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !parent?.id) {
        setLoading(false);
        return;
      }

      try {
        const [childrenData, medicalDataResponse, medicalHistoriesData] = await Promise.all([
          fetchChildren(),
          fetchMedicalData(),
          fetchMedicalHistories()
        ]);
        setChildren(childrenData || []);
        setMedicalData(medicalDataResponse || null);
        setMedicalHistories(medicalHistoriesData || []);
      } catch (err) {
        setError(t('errors.loadFailed', { ns: 'common' }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, parent, t]);

  // Filter children who already have medical history for the current year
  useEffect(() => {
    if (children.length && medicalHistories.length) {
      // Get IDs of children who already have medical history for current year
      const childrenWithHistory = new Set(
        medicalHistories
          .filter(history => history.year === currentYear)
          .map(history => history.child)
      );

      // Filter out children who already have medical history
      const availableChildren = children.filter(child => !childrenWithHistory.has(child.id));
      
      setFilteredChildren(availableChildren);
    } else {
      setFilteredChildren(children);
    }
  }, [children, medicalHistories, currentYear]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    // Handle checkbox inputs
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));

      // Reset related fields when a toggle is turned off
      if (!checked) {
        switch (name) {
          case 'is_vaccinated':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              missing_vaccines: [],
              other_missing_vaccines: ""
            }));
            break;
          case 'has_any_known_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              known_diseases: [],
              other_known_diseases: ""
            }));
            break;
          case 'has_allergies':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              allergies: [],
              other_allergies: ""
            }));
            break;
          case 'has_any_major_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              major_disease: ""
            }));
            break;
          case 'has_psychological_disorder':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              psychological_disorders: [],
              other_psychological_disorders: ""
            }));
            break;
          case 'is_receiving_medication':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              medication: ""
            }));
            break;
          case 'has_any_development_disorder':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              development_disorders: [],
              other_development_disorders: ""
            }));
            break;
          case 'is_being_watched_by_specialist':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              specialist_contact_info: ""
            }));
            break;
          case 'has_worth_mentioning_health_issues':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              worth_mentioning_health_issues: ""
            }));
            break;
          case 'belongs_to_risk_group':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              risk_group_explanation: ""
            }));
            break;
          case 'has_underlying_disease':
            setFormData(prev => ({ 
              ...prev, 
              [name]: checked,
              underlying_diseases: [],
              other_underlying_diseases: ""
            }));
            break;
          default:
            break;
        }
      }
      return;
    }
    
    // Handle normal inputs
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelectChange = (field: string, id: number, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field as keyof typeof prev] as number[], id]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: (prev[field as keyof typeof prev] as number[]).filter(item => item !== id)
      }));
    }
  };

  const submitForm = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const weightNum = parseFloat(formData.weight as string);
      const submitData = {
        ...formData,
        weight: weightNum,
      };

      await createMedicalHistory(submitData);
      navigate('/');
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('addPage.error', { ns: 'medicalHistory' }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Check acknowledgments
    if (!formData.medical_data_acknowledged || !formData.personal_data_acknowledged) {
      setError(t('consent.acknowledgmentRequired', { ns: 'medicalHistory' }));
      return;
    }

    // Validation checks
    const newFieldErrors: {[key: string]: string} = {};
    let hasErrors = false;

    // Basic validation
    if (!formData.child) {
      newFieldErrors.child = t('validation.childRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (!formData.weight) {
      newFieldErrors.weight = t('validation.weightRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    // Convert weight from string to number
    const weightNum = parseFloat(formData.weight as string);
    if (formData.weight && isNaN(weightNum)) {
      newFieldErrors.weight = t('validation.weightInvalid', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    // Additional validation based on conditionals
    if (!formData.is_vaccinated &&
        formData.missing_vaccines.length === 0 &&
        !formData.other_missing_vaccines) {
      newFieldErrors.missing_vaccines = t('validation.missingVaccinesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_any_known_disease &&
        formData.known_diseases.length === 0 &&
        !formData.other_known_diseases) {
      newFieldErrors.known_diseases = t('validation.knownDiseasesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_allergies &&
        formData.allergies.length === 0 &&
        !formData.other_allergies) {
      newFieldErrors.allergies = t('validation.allergiesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_any_major_disease &&
        !formData.major_disease) {
      newFieldErrors.major_disease = t('validation.majorDiseaseRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_psychological_disorder &&
        formData.psychological_disorders.length === 0 &&
        !formData.other_psychological_disorders) {
      newFieldErrors.psychological_disorders = t('validation.psychologicalDisordersRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.is_receiving_medication &&
        !formData.medication) {
      newFieldErrors.medication = t('validation.medicationRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_any_development_disorder &&
        formData.development_disorders.length === 0 &&
        !formData.other_development_disorders) {
      newFieldErrors.development_disorders = t('validation.developmentDisordersRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.is_being_watched_by_specialist &&
        !formData.specialist_contact_info) {
      newFieldErrors.specialist_contact_info = t('validation.specialistContactRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_worth_mentioning_health_issues &&
        !formData.worth_mentioning_health_issues) {
      newFieldErrors.worth_mentioning_health_issues = t('validation.healthIssuesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.belongs_to_risk_group &&
        !formData.risk_group_explanation) {
      newFieldErrors.risk_group_explanation = t('validation.riskGroupRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (formData.has_underlying_disease &&
        formData.underlying_diseases.length === 0 &&
        !formData.other_underlying_diseases) {
      newFieldErrors.underlying_diseases = t('validation.underlyingDiseasesRequired', { ns: 'medicalHistory' });
      hasErrors = true;
    }

    if (hasErrors) {
      setFieldErrors(newFieldErrors);

      // Scroll to the first error
      const firstErrorField = Object.keys(newFieldErrors)[0];
      if (firstErrorField) {
        const element = document.getElementById(firstErrorField);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    // If medical consent is not full, show confirmation dialog
    if (formData.medical_consent_level !== 'full') {
      setShowSubmitConfirmDialog(true);
      return;
    }

    await submitForm();
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (filteredChildren.length === 0 && !loading) {
    return (
      <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 h-full flex flex-col">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t('addPage.title', { ns: 'medicalHistory' })}
        </h2>
        
        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 mb-4">
          <h3 className="text-lg font-bold mb-2">
            {children.length === 0 
              ? t('addPage.noChildren.title', { ns: 'medicalHistory' })
              : t('addPage.allChildrenHaveHistory', { ns: 'medicalHistory' })}
          </h3>
          <p>
            {children.length === 0 
              ? t('addPage.noChildren.message', { ns: 'medicalHistory' })
              : t('addPage.allChildrenHaveHistoryMessage', { ns: 'medicalHistory', year: currentYear })}
          </p>
          <div className="mt-4 flex sm:justify-end">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 mr-2"
            >
              {t('returnButton', { ns: 'medicalHistory' })}
            </button>
            <button
              onClick={() => navigate('/children/add')}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 disabled:pointer-events-none disabled:opacity-50 text-center py-2 px-4"
            >
              {t('addPage.noChildren.addChildButton', { ns: 'medicalHistory' })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card text-card-foreground shadow-sm p-6 h-full flex flex-col">
      <h2 className="text-xl font-semibold text-foreground mb-2">
        {t('addPage.title', { ns: 'medicalHistory' })}
      </h2>
      
      <p className="mb-4 text-muted-foreground">{t('addExplanation', { ns: 'medicalHistory' })}</p>
      
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 mb-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 mb-4">
          <p className="text-green-700">{t('addPage.success', { ns: 'medicalHistory' })}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={`space-y-6 ${submitting ? 'opacity-60 pointer-events-none' : ''}`}>
        {submitting && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}
        {/* Basic Information */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.basicInformation', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label htmlFor="child" className="block text-sm font-medium mb-1.5 leading-none">
              {t('form.child', { ns: 'medicalHistory' })} *
            </label>
            <select
              id="child"
              name="child"
              value={formData.child}
              onChange={handleInputChange}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">{t('form.selectChild', { ns: 'medicalHistory' })}</option>
              {filteredChildren.map(child => (
                <option key={child.id} value={child.id}>
                  {child.first_name} {child.last_name}
                </option>
              ))}
            </select>
            {fieldErrors['child'] && (
              <p className="text-xs text-destructive mt-1">{fieldErrors['child']}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="weight" className="block text-sm font-medium mb-1.5 leading-none">
              {t('form.weight', { ns: 'medicalHistory' })} *
            </label>
            <input
              id="weight"
              name="weight"
              type="number"
              step="0.01"
              min="0"
              max="500"
              value={formData.weight}
              onChange={handleInputChange}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {fieldErrors['weight'] && (
              <p className="text-xs text-destructive mt-1">{fieldErrors['weight']}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="blood_type" className="block text-sm font-medium mb-1.5 leading-none">
              {t('form.bloodType', { ns: 'medicalHistory' })}
            </label>
            <select
              id="blood_type"
              name="blood_type"
              value={formData.blood_type}
              onChange={handleInputChange}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">{t('form.selectBloodType', { ns: 'medicalHistory' })}</option>
              {/* Always use standard English blood types - never translate these medical values */}
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Vaccination Information */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.vaccination', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_vaccinated"
                checked={formData.is_vaccinated}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.isVaccinated', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {!formData.is_vaccinated && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="missing_vaccines" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('missingVaccinesLabel', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.missing_vaccines?.map(vaccine => (
                    <label key={vaccine.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.missing_vaccines.includes(vaccine.id)}
                        onChange={(e) => handleMultiSelectChange('missing_vaccines', vaccine.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{vaccine.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.missing_vaccines && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.missing_vaccines}</p>
                )}
                
                <div>
                  <label htmlFor="other_missing_vaccines" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherMissingVaccines', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_missing_vaccines"
                    name="other_missing_vaccines"
                    type="text"
                    value={formData.other_missing_vaccines}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherMissingVaccinesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Known Diseases */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.knownDiseases', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_known_disease"
                checked={formData.has_any_known_disease}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasKnownDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_known_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="known_diseases" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.knownDiseases', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.known_diseases?.map(disease => (
                    <label key={disease.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.known_diseases.includes(disease.id)}
                        onChange={(e) => handleMultiSelectChange('known_diseases', disease.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{disease.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.known_diseases && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.known_diseases}</p>
                )}
                
                <div>
                  <label htmlFor="other_known_diseases" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherKnownDiseases', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_known_diseases"
                    name="other_known_diseases"
                    type="text"
                    value={formData.other_known_diseases}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherKnownDiseasesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Allergies */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.allergies', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_allergies"
                checked={formData.has_allergies}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasAllergies', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_allergies && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="allergies" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.allergies', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.allergies?.map(allergy => (
                    <label key={allergy.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.allergies.includes(allergy.id)}
                        onChange={(e) => handleMultiSelectChange('allergies', allergy.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{allergy.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.allergies && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.allergies}</p>
                )}
                
                <div>
                  <label htmlFor="other_allergies" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherAllergies', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_allergies"
                    name="other_allergies"
                    type="text"
                    value={formData.other_allergies}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherAllergiesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Major Disease */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.majorDisease', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_major_disease"
                checked={formData.has_any_major_disease}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasMajorDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_major_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="major_disease" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.majorDisease', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="major_disease"
                  name="major_disease"
                  type="text"
                  value={formData.major_disease}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={t('form.majorDiseasePlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.major_disease && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.major_disease}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Psychological Disorders */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.psychologicalDisorders', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_psychological_disorder"
                checked={formData.has_psychological_disorder}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasPsychologicalDisorder', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_psychological_disorder && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="psychological_disorders" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.psychologicalDisorders', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.psychological_disorders?.map(disorder => (
                    <label key={disorder.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.psychological_disorders.includes(disorder.id)}
                        onChange={(e) => handleMultiSelectChange('psychological_disorders', disorder.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{disorder.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.psychological_disorders && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.psychological_disorders}</p>
                )}
                
                <div>
                  <label htmlFor="other_psychological_disorders" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherPsychologicalDisorders', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_psychological_disorders"
                    name="other_psychological_disorders"
                    type="text"
                    value={formData.other_psychological_disorders}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherPsychologicalDisordersPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Medication */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.medication', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_receiving_medication"
                checked={formData.is_receiving_medication}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.isReceivingMedication', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.is_receiving_medication && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="medication" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.medication', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="medication"
                  name="medication"
                  type="text"
                  value={formData.medication}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={t('form.medicationPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.medication && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.medication}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Development Disorders */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.developmentDisorders', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_any_development_disorder"
                checked={formData.has_any_development_disorder}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasDevelopmentDisorder', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_any_development_disorder && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="development_disorders" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.developmentDisorders', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.development_disorders?.map(disorder => (
                    <label key={disorder.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.development_disorders.includes(disorder.id)}
                        onChange={(e) => handleMultiSelectChange('development_disorders', disorder.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{disorder.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.development_disorders && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.development_disorders}</p>
                )}
                
                <div>
                  <label htmlFor="other_development_disorders" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherDevelopmentDisorders', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_development_disorders"
                    name="other_development_disorders"
                    type="text"
                    value={formData.other_development_disorders}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherDevelopmentDisordersPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Specialist */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.specialistCare', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_being_watched_by_specialist"
                checked={formData.is_being_watched_by_specialist}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.isWatchedBySpecialist', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.is_being_watched_by_specialist && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="specialist_contact_info" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.specialistContactInfo', { ns: 'medicalHistory' })} *
                </label>
                <input
                  id="specialist_contact_info"
                  name="specialist_contact_info"
                  type="text"
                  value={formData.specialist_contact_info}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder={t('form.specialistContactInfoPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.specialist_contact_info && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.specialist_contact_info}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Health Issues */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.healthIssues', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_worth_mentioning_health_issues"
                checked={formData.has_worth_mentioning_health_issues}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasHealthIssues', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_worth_mentioning_health_issues && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="worth_mentioning_health_issues" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.healthIssues', { ns: 'medicalHistory' })} *
                </label>
                <textarea
                  id="worth_mentioning_health_issues"
                  name="worth_mentioning_health_issues"
                  value={formData.worth_mentioning_health_issues}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  rows={3}
                  placeholder={t('form.healthIssuesPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.worth_mentioning_health_issues && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.worth_mentioning_health_issues}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Risk Group */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.riskGroup', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="belongs_to_risk_group"
                checked={formData.belongs_to_risk_group}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.belongsToRiskGroup', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.belongs_to_risk_group && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label htmlFor="risk_group_explanation" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.riskGroupExplanation', { ns: 'medicalHistory' })} *
                </label>
                <textarea
                  id="risk_group_explanation"
                  name="risk_group_explanation"
                  value={formData.risk_group_explanation}
                  onChange={handleInputChange}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  rows={3}
                  placeholder={t('form.riskGroupExplanationPlaceholder', { ns: 'medicalHistory' })}
                />
                {fieldErrors.risk_group_explanation && (
                  <p className="text-xs text-destructive mt-1">{fieldErrors.risk_group_explanation}</p>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Underlying Diseases */}
        <div className="bg-muted/50 p-4 rounded-lg border">
          <h3 className="text-base font-semibold mb-3">{t('sections.underlyingDiseases', { ns: 'medicalHistory' })}</h3>
          
          <div className="mb-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="has_underlying_disease"
                checked={formData.has_underlying_disease}
                onChange={handleInputChange}
                className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span>{t('form.hasUnderlyingDisease', { ns: 'medicalHistory' })}</span>
            </label>
          </div>
          
          {formData.has_underlying_disease && (
            <>
              <div className="mb-4 pl-4 border-l-2 border-blue-200">
                <label id="underlying_diseases" className="block text-sm font-medium mb-1.5 leading-none">
                  {t('form.underlyingDiseases', { ns: 'medicalHistory' })} *
                </label>
                
                <div className="space-y-2 mb-4">
                  {medicalData?.underlying_diseases?.map(disease => (
                    <label key={disease.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.underlying_diseases.includes(disease.id)}
                        onChange={(e) => handleMultiSelectChange('underlying_diseases', disease.id, e.target.checked)}
                        className="h-4 w-4 mr-2 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span>{disease.name}</span>
                    </label>
                  ))}
                </div>
                
                {fieldErrors.underlying_diseases && (
                  <p className="text-destructive text-xs mt-1 mb-2 font-bold">{fieldErrors.underlying_diseases}</p>
                )}
                
                <div>
                  <label htmlFor="other_underlying_diseases" className="block text-sm font-medium mb-1.5 leading-none">
                    {t('form.otherUnderlyingDiseases', { ns: 'medicalHistory' })}
                  </label>
                  <input
                    id="other_underlying_diseases"
                    name="other_underlying_diseases"
                    type="text"
                    value={formData.other_underlying_diseases}
                    onChange={handleInputChange}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    placeholder={t('form.otherUnderlyingDiseasesPlaceholder', { ns: 'medicalHistory' })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* GDPR Consent Section */}
        <div className="p-4 rounded-lg border-2 border-primary/30 bg-primary/5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">{t('consent.sectionTitle', { ns: 'medicalHistory' })}</h3>
          </div>

          {/* Collapsible GDPR Information Document */}
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowGdprInfo(!showGdprInfo)}
              className="text-sm text-primary hover:text-primary/80 underline font-medium flex items-center gap-1"
            >
              {showGdprInfo
                ? t('consent.hideFullInfo', { ns: 'medicalHistory' })
                : t('consent.readFullInfo', { ns: 'medicalHistory' })
              }
              {showGdprInfo ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>

            {showGdprInfo && (
              <div className="mt-3 p-4 bg-white rounded-md border border-gray-200 max-h-[400px] overflow-y-auto text-sm text-muted-foreground">
                <h3 className="text-base font-bold mb-3">{t('consent.infoDocumentTitle', { ns: 'medicalHistory' })}</h3>
                <div
                  dangerouslySetInnerHTML={{ __html: t('consent.infoDocumentHtml', { ns: 'medicalHistory' }) }}
                />
              </div>
            )}
          </div>

          {/* Medical Data Consent */}
          <div className="mb-5">
            <h4 className="text-sm font-semibold mb-2">{t('consent.medicalConsentTitle', { ns: 'medicalHistory' })}</h4>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="full"
                  checked={formData.medical_consent_level === 'full'}
                  onChange={() => setFormData({ ...formData, medical_consent_level: 'full', medical_consent_explanation: '' })}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.fullConsent', { ns: 'medicalHistory' })}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="partial"
                  checked={formData.medical_consent_level === 'partial'}
                  onChange={() => {
                    setFormData({ ...formData, medical_consent_level: 'partial' });
                    setShowConsentWarningDialog(true);
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.partialConsent', { ns: 'medicalHistory' })}</span>
              </label>
              {formData.medical_consent_level === 'partial' && (
                <div className="pl-7">
                  <textarea
                    value={formData.medical_consent_explanation}
                    onChange={(e) => setFormData({ ...formData, medical_consent_explanation: e.target.value })}
                    placeholder={t('consent.explainPlaceholder', { ns: 'medicalHistory' })}
                    className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    rows={3}
                  />
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="medical_consent_level"
                  value="none"
                  checked={formData.medical_consent_level === 'none'}
                  onChange={() => {
                    setFormData({ ...formData, medical_consent_level: 'none', medical_consent_explanation: '' });
                    setShowConsentWarningDialog(true);
                  }}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.noConsent', { ns: 'medicalHistory' })}</span>
              </label>
            </div>
          </div>

          {/* Personal Data Consent */}
          <div className="mb-5">
            <h4 className="text-sm font-semibold mb-2">{t('consent.personalConsentTitle', { ns: 'medicalHistory' })}</h4>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="full"
                  checked={formData.personal_consent_level === 'full'}
                  onChange={() => setFormData({ ...formData, personal_consent_level: 'full', personal_consent_explanation: '' })}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.fullConsent', { ns: 'medicalHistory' })}</span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="partial"
                  checked={formData.personal_consent_level === 'partial'}
                  onChange={() => setFormData({ ...formData, personal_consent_level: 'partial' })}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.partialConsent', { ns: 'medicalHistory' })}</span>
              </label>
              {formData.personal_consent_level === 'partial' && (
                <div className="pl-7">
                  <textarea
                    value={formData.personal_consent_explanation}
                    onChange={(e) => setFormData({ ...formData, personal_consent_explanation: e.target.value })}
                    placeholder={t('consent.explainPlaceholderPersonal', { ns: 'medicalHistory' })}
                    className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    rows={3}
                  />
                </div>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="personal_consent_level"
                  value="none"
                  checked={formData.personal_consent_level === 'none'}
                  onChange={() => setFormData({ ...formData, personal_consent_level: 'none', personal_consent_explanation: '' })}
                  className="h-4 w-4 mt-0.5"
                />
                <span className="text-sm">{t('consent.noConsentPersonal', { ns: 'medicalHistory' })}</span>
              </label>
            </div>
          </div>

          {/* Acknowledgment checkboxes */}
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.medical_data_acknowledged}
                onChange={(e) => setFormData({ ...formData, medical_data_acknowledged: e.target.checked })}
                className="h-5 w-5 mt-0.5 rounded border-input focus:ring-ring"
              />
              <span className="text-sm font-medium">{t('consent.medicalDataAcknowledged', { ns: 'medicalHistory' })} *</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.personal_data_acknowledged}
                onChange={(e) => setFormData({ ...formData, personal_data_acknowledged: e.target.checked })}
                className="h-5 w-5 mt-0.5 rounded border-input focus:ring-ring"
              />
              <span className="text-sm font-medium">{t('consent.personalDataAcknowledged', { ns: 'medicalHistory' })} *</span>
            </label>
          </div>
        </div>

        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start items-center mt-6">
          <div className="flex items-center h-5">
            <input
              id="termsAccepted"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="h-4 w-4 rounded border-input focus:ring-ring"
              required
            />
          </div>
          <div className="ml-3 text-base">
            <label htmlFor="termsAccepted" className="font-medium text-foreground">
              {t('form.termsAndConditionsLabel', { ns: 'medicalHistory' })}
            </label>
            <p className="text-muted-foreground">
              {t('form.termsAndConditionsFullTextPart1', { ns: 'medicalHistory' })}{' '}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-blue-600 hover:underline focus:outline-none font-medium"
              >
                {t('form.termsAndConditionsLink', { ns: 'medicalHistory' })}
              </button>
              {' '}{t('form.termsAndConditionsFullTextPart2', { ns: 'medicalHistory' })}
            </p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-end mt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base"
          >
            {t('cancel', { ns: 'common' })}
          </button>
          
          <button
            type="submit"
            disabled={submitting || !termsAccepted || !formData.medical_data_acknowledged || !formData.personal_data_acknowledged}
            className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors shadow h-9 px-4 py-2 py-2 px-4 sm:py-3 sm:px-6 text-sm sm:text-base ${
              termsAccepted && formData.medical_data_acknowledged && formData.personal_data_acknowledged && !submitting ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {submitting ? t('submitting', { ns: 'common' }) : t('submitButton', { ns: 'medicalHistory' })}
          </button>
        </div>
      </form>

      {showTermsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div ref={modalRef} className="relative mx-auto p-6 border w-full max-w-2xl shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl leading-6 font-bold text-foreground">{t('form.termsAndConditionsTitle', { ns: 'medicalHistory' })}</h3>
              <button
                type="button"
                className="text-muted-foreground bg-transparent hover:bg-muted hover:text-foreground rounded-lg text-sm p-1.5 ml-auto inline-flex items-center"
                onClick={() => setShowTermsModal(false)}
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-2 text-left text-sm text-muted-foreground max-h-[70vh] overflow-y-auto pr-2">
              <p className="whitespace-pre-line">
                {t('form.termsAndConditionsContent', { ns: 'medicalHistory' })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Consent Warning Dialog — informational, triggered by partial/none medical consent */}
      {showConsentWarningDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div ref={consentWarningRef} className="relative mx-auto p-6 border w-full max-w-md shadow-xl rounded-lg bg-white">
            <h3 className="text-lg font-bold text-foreground mb-3">{t('consent.warningTitle', { ns: 'medicalHistory' })}</h3>
            <p className="text-sm text-muted-foreground mb-5">{t('consent.warningText', { ns: 'medicalHistory' })}</p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowConsentWarningDialog(false)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
              >
                {t('consent.ok', { ns: 'medicalHistory' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Dialog — shown when medical consent is not full */}
      {showSubmitConfirmDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50 px-4">
          <div ref={submitConfirmRef} className="relative mx-auto p-6 border w-full max-w-md shadow-xl rounded-lg bg-white">
            <h3 className="text-lg font-bold text-foreground mb-3">{t('consent.confirmSubmitTitle', { ns: 'medicalHistory' })}</h3>
            <p className="text-sm text-muted-foreground mb-2">{t('consent.warningText', { ns: 'medicalHistory' })}</p>
            <p className="text-sm text-muted-foreground font-medium mb-5">{t('consent.confirmSubmitMessage', { ns: 'medicalHistory' })}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowSubmitConfirmDialog(false)}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2"
              >
                {t('consent.goBack', { ns: 'medicalHistory' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowSubmitConfirmDialog(false);
                  submitForm();
                }}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
              >
                {t('consent.proceed', { ns: 'medicalHistory' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 